import { Service } from '@angular/core';
import dayjs from 'dayjs/esm';
import { distinctUntilChanged, filter, Observable, Subject } from 'rxjs';

import {
  AcceptedResourceItemType,
  AnyResource,
  Conversation,
  FileProcessType,
  PermissionItem,
  toFileProcessType,
  TreeEntry,
} from '@models';
import type { VoteParent } from '@shared/components/vote/types';

export enum AppEventType {
  RESET_ITEM_DETAIL = 'RESET_ITEM_DETAIL',
  REFRESH_EVENT_STRUCTURE = 'refreshEventStructure',
  REFRESH_ROOM_LIST = 'refreshRoomList',
  REFRESH_EVENT = 'refreshEvent',
  REFRESH_USER = 'refreshUser',
  REFRESH_TREE_LIST = 'refreshTreeList',
  RESET_ROOM_LIST = 'resetRoomList',
  OPEN_FILE_LINK = 'openFileLink',
  REFRESH_ITEMS_LIST = 'refreshItemsList',
  OPEN_DISCUSSION_LINK = 'openDiscussionLink',
  USER_SESSION_EXPIRATION = 'userSessionExpiration_event',
  LOAD_PROFILE_IMAGE = 'loadProfileImage',
  NEW_BILLING_STEP = 'newBillingStep',
  SET_VIEW = 'setView',
  GO_TO_MONTH = 'goToMonth',
  GO_TO_WEEK = 'goToWeek',
  GO_TO_DAY = 'goToDay',
  SET_CALENDAR_TITLE = 'setCalendarTitle',
  ADD_COMMENT = 'addComment',
  REFRESH_CONVERSATIONS_LIST_EVENT = 'refreshConversationsList_event',
  NO_LOCATION_FIND = 'noLocationFind',
  SET_FINDER_DESTINATION = 'setFinderDestination',
  SELECT_CONVERSATION = 'selectConversation',
  OPEN_VOTE_LINK = 'openVoteLink',
  REFRESH_TASK_LIST = 'refreshTaskList',
  SHOW_SIGNATURE_STATUS = 'showSignatureStatus',
  REFRESH_SIGNING_ROOM = 'refreshSigningRoom',
  REFRESH_LIST_BY_ENTITY_TYPE = 'refreshListByEntityType',
  INFINITE_SCROLL_EVENT = 'aw-infinite-scroll-event',
  REFRESH_LIST_BY_FILTER = 'refreshListByFilter',
  REFRESH_LIST_BY_STATUS = 'refreshListByStatus',
  UPDATE_RESOURCE_LIST = 'updateResourceList',
  RESET_ROOM_EMPTY = 'resetRoomEmpty',
  CLOSE_SEARCH = 'closeSearch',
  RESET_PERMISSIONS_SELECTIONS = 'resetPermissionsSelections',
  REFRESH_RECENT = 'refreshRecent',
  INFINITE_TIMELINE_SCROLL_EVENT = 'aw-infinite-timeline-scroll-event',
  ITEM_RENAMED = 'itemRenamed',
  OPEN_DIFFERENT_SESSION_DIALOG = 'openDifferentSessionDialog',
  CLEAR_UPLOADS = 'CLEAR_UPLOADS',
}

export interface AppEventMap {
  [AppEventType.RESET_ITEM_DETAIL]: never;
  [AppEventType.REFRESH_EVENT_STRUCTURE]?: number;
  [AppEventType.REFRESH_ROOM_LIST]: never;
  [AppEventType.REFRESH_EVENT]: number;
  [AppEventType.REFRESH_USER]: never;
  [AppEventType.REFRESH_TREE_LIST]: never;
  [AppEventType.RESET_ROOM_LIST]: never;
  [AppEventType.OPEN_FILE_LINK]: {
    rid: number;
    processType?: FileProcessType;
    isSigning?: boolean;
  };
  [AppEventType.REFRESH_ITEMS_LIST]: string;
  [AppEventType.OPEN_DISCUSSION_LINK]: number;
  [AppEventType.USER_SESSION_EXPIRATION]: never;
  [AppEventType.LOAD_PROFILE_IMAGE]: { rid: number; image?: string };
  [AppEventType.NEW_BILLING_STEP]: number;
  [AppEventType.SET_VIEW]: string;
  [AppEventType.GO_TO_MONTH]: dayjs.Dayjs;
  [AppEventType.GO_TO_WEEK]: { start: dayjs.Dayjs; end: dayjs.Dayjs };
  [AppEventType.GO_TO_DAY]: { start: dayjs.Dayjs; end: dayjs.Dayjs };
  [AppEventType.SET_CALENDAR_TITLE]: string;
  [AppEventType.ADD_COMMENT]: { flag: boolean; count: number };
  [AppEventType.REFRESH_CONVERSATIONS_LIST_EVENT]: never;
  [AppEventType.NO_LOCATION_FIND]: never;
  [AppEventType.SET_FINDER_DESTINATION]: {
    finderDestination: {
      rid: number;
      type: number;
      permission: number;
      parentSubtype: number;
      level?: number;
    };
    currentPathList?: TreeEntry[];
  };
  [AppEventType.SELECT_CONVERSATION]: Nullable<Conversation>;
  [AppEventType.OPEN_VOTE_LINK]: {
    rid: number | string;
    parentResource?: Nullable<VoteParent>;
  };
  [AppEventType.REFRESH_TASK_LIST]: never;
  [AppEventType.SHOW_SIGNATURE_STATUS]: number;
  [AppEventType.REFRESH_SIGNING_ROOM]?: boolean;
  [AppEventType.REFRESH_LIST_BY_ENTITY_TYPE]: number;
  [AppEventType.INFINITE_SCROLL_EVENT]: boolean;
  [AppEventType.REFRESH_LIST_BY_FILTER]?: number;
  [AppEventType.REFRESH_LIST_BY_STATUS]?: number;
  [AppEventType.UPDATE_RESOURCE_LIST]: AcceptedResourceItemType;
  [AppEventType.RESET_ROOM_EMPTY]: boolean;
  [AppEventType.CLOSE_SEARCH]: never;
  [AppEventType.RESET_PERMISSIONS_SELECTIONS]: PermissionItem[];
  [AppEventType.REFRESH_RECENT]: never;
  [AppEventType.INFINITE_TIMELINE_SCROLL_EVENT]: boolean;
  [AppEventType.ITEM_RENAMED]: AnyResource;
  [AppEventType.OPEN_DIFFERENT_SESSION_DIALOG]: never;
  [AppEventType.CLEAR_UPLOADS]: never;
}

export class AppEvent<T extends keyof AppEventMap> {
  public type: T;
  public payload: AppEventMap[T];

  constructor(type: T, payload: AppEventMap[T]);
  constructor(type: T, payload?: undefined);
  constructor(type: T, payload?: AppEventMap[T]) {
    this.type = type;
    this.payload = payload as AppEventMap[T];
  }
}

@Service()
export class EventQueueService {
  eventBroker = new Subject<AppEvent<keyof AppEventMap>>();

  on<T extends keyof AppEventMap>(eventType: T): Observable<AppEvent<T>> {
    return this.eventBroker.pipe(
      filter((event): event is AppEvent<T> => event.type === eventType),
      distinctUntilChanged()
    );
  }

  dispatch<T extends keyof AppEventMap>(event: AppEvent<T>): void {
    this.eventBroker.next(event);
  }

  listen<T extends keyof AppEventMap>(eventType: T): Observable<AppEvent<T>> {
    return this.on(eventType);
  }

  openResourceFromQueryParams = (params: {
    isModalOpen: boolean;
    queryParams: Record<string, string>;
  }) => {
    if (params.isModalOpen) {
      return;
    }

    const { file, signprocess, discussion, vote } = params.queryParams;

    if (file) {
      this.dispatch(
        new AppEvent(AppEventType.OPEN_FILE_LINK, {
          rid: +file,
          processType: toFileProcessType(signprocess),
        })
      );
    }

    if (discussion) {
      this.dispatch(
        new AppEvent(AppEventType.OPEN_DISCUSSION_LINK, +discussion)
      );
    }

    if (vote) {
      this.dispatch(
        new AppEvent(AppEventType.OPEN_VOTE_LINK, {
          rid: vote,
        })
      );
    }
  };
}
